import React, { useState, useCallback, useEffect } from 'react';
import ID from './UUID';
import IntlMessages from './language-provider/IntlMessages';

const DynamicOptionList = (props) => {
	const [element, setElement] = useState(props.element);
	const [data, setData] = useState(props.data);
	const [dirty, setDirty] = useState(false);

	// Update local state when props change
	useEffect(() => {
		setElement(props.element);
	}, [props.element]);

	useEffect(() => {
		setData(props.data);
	}, [props.data]);

	const _setValue = useCallback((text) => {
		return text.replace(/[^A-Z0-9]+/gi, '_').toLowerCase();
	}, []);

	const editOption = useCallback(
		(option_index, e) => {
			const this_element = { ...element };
			const val =
				this_element.options[option_index].value !==
				_setValue(this_element.options[option_index].text)
					? this_element.options[option_index].value
					: _setValue(e.target.value);

			this_element.options[option_index].text = e.target.value;
			this_element.options[option_index].value = val;
			this_element.options[option_index].rating = val;
			setElement(this_element);
			setDirty(true);
		},
		[element, _setValue]
	);

	const editValue = useCallback(
		(option_index, e) => {
			const this_element = { ...element };
			const val =
				e.target.value === '' ? _setValue(this_element.options[option_index].text) : e.target.value;
			this_element.options[option_index].value = val;
			setElement(this_element);
			setDirty(true);
		},
		[element, _setValue]
	);

	const editRating = useCallback(
		(option_index, e) => {
			const this_element = { ...element };
			const val =
				e.target.rating === ''
					? _setValue(this_element.options[option_index].text)
					: e.target.value;
			this_element.options[option_index].rating = val;
			setElement(this_element);
			setDirty(true);
		},
		[element, _setValue]
	);

	// eslint-disable-next-line
	const editOptionCorrect = useCallback(
		(option_index, e) => {
			const this_element = { ...element };
			// eslint-disable-next-line
			if (this_element.options[option_index].hasOwnProperty('correct')) {
				delete this_element.options[option_index].correct;
			} else {
				this_element.options[option_index].correct = true;
			}
			setElement(this_element);
			props.updateElement.call(props.preview, this_element);
		},
		[element, props]
	);

	const updateOption = useCallback(() => {
		// to prevent ajax calls with no change
		if (dirty) {
			props.updateElement.call(props.preview, element);
			setDirty(false);
		}
	}, [dirty, element, props]);

	const addOption = useCallback(
		(index) => {
			const this_element = { ...element };
			this_element.options.splice(index + 1, 0, {
				value: '',
				text: '',
				key: `_option_${ID.uuid()}`
			});
			props.updateElement.call(props.preview, this_element);
		},
		[element, props]
	);

	const removeOption = useCallback(
		(index) => {
			const this_element = { ...element };
			this_element.options.splice(index, 1);
			props.updateElement.call(props.preview, this_element);
		},
		[element, props]
	);

	if (dirty) {
		// eslint-disable-next-line
		element.dirty = true;
	}

	return (
		<div className="dynamic-option-list">
			<ul>
				<li>
					<div className="row">
						<div className="col-sm-4">
							<b>
								<IntlMessages id="options" />
							</b>
						</div>
						{props.can_have_option_value && (
							<div className="col-sm-2">
								<b>
									<IntlMessages id="value" />
								</b>
							</div>
						)}
						{!['MUI_Checkbox', 'MUI_RadioButton', 'StatePicker', 'MUI_Dropdown'].includes(
							props.element.key
						) &&
							props.can_have_option_value &&
							props.can_have_option_correct && (
								<div className="col-sm-4">
									<b>
										<IntlMessages id="correct" />
									</b>
								</div>
							)}
					</div>
				</li>
				{props.element.options.map((option, index) => {
					const this_key = `edit_${option.key}`;
					const val = option.value !== _setValue(option.text) ? option.value : '';
					return (
						<li className="clearfix" key={this_key}>
							<div className="row">
								<div className="col-sm-4">
									<input
										tabIndex={index + 1}
										className="form-control"
										style={{ width: '100%' }}
										type="text"
										name={`text_${index}`}
										placeholder="Option text"
										value={option.text}
										onBlur={updateOption}
										onChange={(e) => editOption(index, e)}
									/>
								</div>
								{props.isReview &&
									props.can_have_option_value &&
									props.element.key !== 'StatePicker' && (
										<div className="col-sm-2">
											<input
												className="form-control"
												type="text"
												name={`value_${index}`}
												value={val}
												required={props.include_in_total_score}
												placeholder={props.include_in_total_score ? '*' : ''}
												onChange={(e) => editValue(index, e)}
											/>
										</div>
									)}
								{!['MUI_Checkbox', 'MUI_RadioButton', 'StatePicker', 'MUI_Dropdown'].includes(
									props.element.key
								) &&
									props.can_have_option_value &&
									props.can_have_option_correct && (
										<div className="col-sm-1">
											<input
												className="form-control"
												type="checkbox"
												value="1"
												onChange={(e) => editOptionCorrect(index, e)}
												// eslint-disable-next-line
												checked={option.hasOwnProperty('correct')}
											/>
										</div>
									)}
								<div className="col-sm-3">
									<div className="dynamic-options-actions-buttons">
										<button onClick={() => addOption(index)} className="btn btn-success">
											<i className="fas fa-plus-circle"></i>
										</button>
										{index > 0 && (
											<button onClick={() => removeOption(index)} className="btn btn-danger">
												<i className="fas fa-minus-circle"></i>
											</button>
										)}
									</div>
								</div>
							</div>
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export default DynamicOptionList;
